import { useEffect, useState } from "react";
import { Check, Copy, Plus, Terminal, Trash2, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg, fmtDate } from "@/lib/api";
import ConnecteurSonde from "@/components/ConnecteurSonde";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MARQUES = ["loxone", "unv", "unifi", "reyee", "daikin", "heiwa", "autre"];
const INTERVALLES = [5, 10, 15, 30, 60];
const ETATS = {
  en_ligne: { label: "En ligne", classe: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  hors_ligne: { label: "Hors ligne", classe: "text-red-700 bg-red-50 border-red-200" },
  en_attente: { label: "Jamais vu", classe: "text-slate-500 bg-slate-50 border-slate-200" },
};

export default function AdminSondes() {
  const [sondes, setSondes] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ designation: "", marque: "loxone", intervalle_min: 10 });
  const [copie, setCopie] = useState(null);
  const [connecteur, setConnecteur] = useState(null);

  const load = () => api.get("/admin/sondes").then(({ data }) => setSondes(data));

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  const chercher = async () => {
    if (recherche.trim().length < 2) return;
    const { data } = await api.get("/admin/clients/search", { params: { q: recherche } });
    setClients(data);
  };

  const creer = async (client) => {
    if (form.designation.trim().length < 2) return toast.error("Indiquez le nom de l'appareil");
    try {
      await api.post("/admin/sondes", { ...form, axonaut_id: client.axonaut_id, site_id: "" });
      toast.success("Appareil surveillé ajouté");
      setForm({ ...form, designation: "" });
      setClients([]);
      setRecherche("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Création impossible"));
    }
  };

  const supprimer = async (s) => {
    await api.delete(`/admin/sondes/${s.id}`);
    toast.success("Surveillance retirée");
    load();
  };

  const url = (s) => `${API}/pings/${s.token}`;

  const copier = async (s) => {
    await navigator.clipboard.writeText(url(s));
    setCopie(s.id);
    toast.success("URL copiée");
    setTimeout(() => setCopie(null), 2500);
  };

  const tester = async (s) => {
    try {
      await api.get(`/pings/${s.token}`);
      toast.success("Signe de vie reçu : l'appareil passe en ligne");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Test échoué"));
    }
  };

  return (
    <div className="space-y-7" data-testid="admin-sondes">
      <div>
        <div className="bk-label mb-2">Surveillance active</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Ping des appareils</h1>
        <p className="text-sm text-slate-600 mt-2">
          Chaque appareil reçoit sa propre URL à appeler toutes les X minutes (« je vais bien »). S'il se tait pendant
          deux intervalles, l'app ouvre automatiquement un ticket bloquant et prévient l'équipe. C'est ce qui détecte les
          coupures de courant, d'internet et les appareils morts, sans rien installer chez le client.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="bk-label">Ajouter un appareil à surveiller</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input
            data-testid="sonde-designation"
            value={form.designation}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
            placeholder="Nom de l'appareil (ex : Miniserver Guzargues)"
            className="h-11"
          />
          <select
            data-testid="sonde-marque"
            value={form.marque}
            onChange={(e) => setForm({ ...form, marque: e.target.value })}
            className="h-11 border border-slate-200 rounded-lg px-3 text-sm"
          >
            {MARQUES.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            data-testid="sonde-intervalle"
            value={form.intervalle_min}
            onChange={(e) => setForm({ ...form, intervalle_min: Number(e.target.value) })}
            className="h-11 border border-slate-200 rounded-lg px-3 text-sm"
          >
            {INTERVALLES.map((i) => (
              <option key={i} value={i}>toutes les {i} min</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="sonde-client-search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && chercher()}
            placeholder="Client…"
            className="h-11"
          />
          <Button data-testid="sonde-search-btn" onClick={chercher} variant="outline" className="h-11">
            Rechercher
          </Button>
        </div>
        {clients.map((c) => (
          <button
            key={c.axonaut_id}
            data-testid={`sonde-client-${c.axonaut_id}`}
            onClick={() => creer(c)}
            className="w-full text-left rounded-lg border border-slate-200 px-3 py-2 text-sm hover:border-[#005CFF] flex items-center justify-between"
          >
            <span>{c.nom}</span>
            <Plus size={15} className="text-[#005CFF]" />
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <div className="bk-label">Appareils surveillés ({sondes.length})</div>
        {sondes.map((s) => {
          const e = ETATS[s.etat] || ETATS.en_attente;
          return (
            <div key={s.id} data-testid={`sonde-${s.id}`} className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {s.etat === "hors_ligne" ? <WifiOff size={14} className="text-red-500" /> : <Wifi size={14} className="text-emerald-500" />}
                    {s.designation}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.client} · {s.site_nom} · {s.marque} · signe attendu toutes les {s.intervalle_min} min ·{" "}
                    {s.dernier_signe ? `dernier ${fmtDate(s.dernier_signe)}` : "aucun signe reçu"}
                    {s.ticket_reference ? ` · ticket ${s.ticket_reference}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full border ${e.classe}`} data-testid={`sonde-etat-${s.id}`}>
                    {e.label}
                  </span>
                  <button data-testid={`sonde-delete-${s.id}`} onClick={() => supprimer(s)} className="text-slate-300 hover:text-red-500">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <code className="block text-[11px] text-slate-500 break-all font-mono-bk">{url(s)}</code>
              <div className="flex gap-4">
                <button
                  data-testid={`sonde-copy-${s.id}`}
                  onClick={() => copier(s)}
                  className="text-xs font-semibold text-[#005CFF] inline-flex items-center gap-1.5 hover:underline"
                >
                  {copie === s.id ? <Check size={13} /> : <Copy size={13} />} Copier l'URL
                </button>
                <button
                  data-testid={`sonde-test-${s.id}`}
                  onClick={() => tester(s)}
                  className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:text-[#005CFF]"
                >
                  <Wifi size={13} /> Envoyer un signe de vie (test)
                </button>
                <button
                  data-testid={`sonde-connecteur-${s.id}`}
                  onClick={() => setConnecteur(connecteur === s.id ? null : s.id)}
                  className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:text-[#005CFF]"
                >
                  <Terminal size={13} /> {connecteur === s.id ? "Masquer le connecteur" : "Connecteur / script à coller"}
                </button>
              </div>
              {connecteur === s.id && <ConnecteurSonde url={url(s)} intervalle={s.intervalle_min} marque={s.marque} />}
            </div>
          );
        })}
        {sondes.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="sondes-empty">
            Aucun appareil surveillé. Ajoutez-en un : chaque appareil affichera ensuite son URL et le connecteur prêt à
            coller (Loxone en natif, script sentinelle pour NVR UNV et Reyee, cron pour UniFi, tâche planifiée Windows).
          </p>
        )}
      </div>
    </div>
  );
}
